<template>
  <div class="comparison-block">
    <p v-if="block.title" class="comparison-title">{{ block.title }}</p>
    <div class="comparison-grid">
      <!-- Correct column -->
      <div class="comparison-column comparison-column--correct">
        <div class="comparison-header comparison-header--correct">
          <q-icon name="check_circle" size="16px" class="comparison-header-icon" />
          <span class="comparison-header-label">{{ block.correct.label || 'Correcto' }}</span>
        </div>
        <ul class="comparison-items">
          <li
            v-for="(item, idx) in block.correct.items"
            :key="idx"
            class="comparison-item comparison-item--correct"
            :class="{ 'comparison-item--alt': idx % 2 === 1 }"
          >
            <span class="comparison-item-bullet comparison-item-bullet--correct" aria-hidden="true"></span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <!-- Incorrect column -->
      <div class="comparison-column comparison-column--incorrect">
        <div class="comparison-header comparison-header--incorrect">
          <q-icon name="cancel" size="16px" class="comparison-header-icon" />
          <span class="comparison-header-label">{{ block.incorrect.label || 'Incorrecto' }}</span>
        </div>
        <ul class="comparison-items">
          <li
            v-for="(item, idx) in block.incorrect.items"
            :key="idx"
            class="comparison-item comparison-item--incorrect"
            :class="{ 'comparison-item--alt': idx % 2 === 1 }"
          >
            <span class="comparison-item-bullet comparison-item-bullet--incorrect" aria-hidden="true"></span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ComparisonSide {
  label?: string;
  items: string[];
}

interface ComparisonBlock {
  type: 'comparison';
  title?: string;
  correct: ComparisonSide;
  incorrect: ComparisonSide;
}

defineProps<{ block: ComparisonBlock }>();
</script>

<style scoped lang="scss">
.comparison-block {
  margin: 24px 0;
}

.comparison-title {
  font-size: var(--oa-text-base);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 12px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid var(--oa-border);
  border-radius: var(--oa-radius-lg);
  overflow: hidden;

  /* Stack vertically on mobile */
  @media (max-width: 639px) {
    grid-template-columns: 1fr;
  }
}

.comparison-column {
  display: flex;
  flex-direction: column;
}

.comparison-column--incorrect {
  border-left: 1px solid var(--oa-border);

  @media (max-width: 639px) {
    border-left: none;
    border-top: 1px solid var(--oa-border);
  }
}

.comparison-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
}

.comparison-header--correct {
  background: color-mix(in srgb, var(--color-teal-wave) 12%, transparent);

  .comparison-header-icon,
  .comparison-header-label {
    color: var(--color-teal-wave);
  }
}

.comparison-header--incorrect {
  background: color-mix(in srgb, var(--color-coral) 12%, transparent);

  .comparison-header-icon,
  .comparison-header-label {
    color: var(--color-coral);
  }
}

.comparison-header-label {
  font-size: var(--oa-text-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.comparison-items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.comparison-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 16px;
  font-size: var(--oa-text-base);
  color: var(--oa-text-primary);
  background: transparent;
}

.comparison-item--alt {
  background: var(--oa-bg-3);
}

.comparison-item-bullet {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 7px;
}

.comparison-item-bullet--correct {
  background: var(--color-teal-wave);
}

.comparison-item-bullet--incorrect {
  background: var(--color-coral);
}
</style>
